import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router";
import { Header } from "../common/Header";
import { OrderDetails } from "./OrderDetails";
import { cancelOrder, getOrderDetails } from "@nusrif/services";
import { OrderData } from "@nusrif/types";
import { Button } from "../ui/button";
import { XCircle } from "lucide-react";
import { useNotificationHelpers } from "@nusrif/hooks";

export const OrderDetailPage = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<OrderData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelInProgress, setCancelInProgress] = useState(false);
  const { showSuccess, showError } = useNotificationHelpers();

  useEffect(() => {
    if (!orderId) {
      setError("Order not found");
      setLoading(false);
      return;
    }
    const fetchOrder = async () => {
      setLoading(true);
      try {
        const response = await getOrderDetails(orderId);
        setOrder(response.data.storeOrder);
      } catch (err) {
        setError("Failed to fetch order details");
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [orderId]);

  const handleCancelOrder = async () => {
    if (!order) return;
    setCancelInProgress(true);
    try {
      await cancelOrder(orderId!);
      showSuccess("Order Cancelled!", "Your order has been cancelled.");
      setOrder({ ...order, status: "Cancelled" });
    } catch (err) {
      showError("Failed to Cancel", "Please try again later.");
    } finally {
      setCancelInProgress(false);
    }
  };

  if (loading) {
    return (
      <>
        <Header
          title="Order Details"
          showBackButton={true}
          showCartButton={false}
        />
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-primary-blue border-t-transparent rounded-full animate-spin" />
        </div>
      </>
    );
  }
  if (error || !order) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Header
          title="Order Details"
          showBackButton={true}
          showCartButton={false}
        />
        <div className="text-center">
          <XCircle className="w-10 h-10 text-red-500 mx-auto mb-2" />
          <h2 className="text-xl font-bold text-black mb-2">Order Not Found</h2>
          <p className="text-gray-600">
            {error || "The order you're looking for doesn't exist."}
          </p>
          <Button className="mt-6" onClick={() => navigate(-1)}>
            Go Back
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen w-full bg-gray-50">
      <Header title="Order Details" showBackButton={true} />
      <div className="pt-20 pb-32 px-5 max-w-2xl mx-auto space-y-6">
        {/* Order Details */}
        <OrderDetails orderData={order} />
        {/* Cancel Button */}
        {order.status === "PaymentSettled" && (
          <div className="pt-4">
            <Button
              className="w-full h-12 font-semibold rounded-full bg-red-500 hover:bg-red-600 text-white text-lg"
              onClick={handleCancelOrder}
              disabled={cancelInProgress}
            >
              {cancelInProgress ? "Cancelling..." : "Cancel Order"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
